import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export const AVAILABLE_SIZES = [
  '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56',
  'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL',
];

export const PREDEFINED_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Red', value: '#FF0000' },
  { name: 'Blue', value: '#0000FF' },
  { name: 'Green', value: '#00FF00' },
  { name: 'Yellow', value: '#FFFF00' },
  { name: 'Orange', value: '#FFA500' },
  { name: 'Purple', value: '#800080' },
  { name: 'Pink', value: '#FFC0CB' },
  { name: 'Brown', value: '#A52A2A' },
  { name: 'Gray', value: '#808080' },
  { name: 'Navy', value: '#000080' },
];

const VARIANT_LABELS = {
  color: 'Renk',
  Color: 'Renk',
  renk: 'Renk',
  Renk: 'Renk',
  size: 'Beden',
  Size: 'Beden',
  beden: 'Beden',
  Beden: 'Beden',
};

function emptyVariantRow(sample) {
  if (sample && typeof sample === 'object') {
    const row = {};
    Object.keys(sample).forEach((k) => {
      if (k === 'stock') row[k] = 0;
      else if (k === 'price') row[k] = '';
      else row[k] = '';
    });
    if (!('stock' in row)) row.stock = 0;
    return row;
  }
  return { color: '', size: '', stock: 0, barcode: '', price: '' };
}

function getVariantColumns(variants) {
  if (!variants?.length) return ['color', 'size', 'stock', 'barcode', 'price'];
  const keys = new Set();
  variants.forEach((v) => {
    if (!v || typeof v !== 'object') return;
    Object.keys(v).forEach((k) => keys.add(k));
  });
  const ordered = [];
  ['color', 'Color', 'renk', 'Renk', 'size', 'Size', 'beden', 'Beden', 'name', 'value'].forEach((k) => {
    if (keys.has(k)) {
      ordered.push(k);
      keys.delete(k);
    }
  });
  ['stock', 'price', 'barcode', 'sku'].forEach((k) => {
    if (keys.has(k)) {
      ordered.push(k);
      keys.delete(k);
    }
  });
  keys.forEach((k) => ordered.push(k));
  return ordered;
}

/**
 * @param {{ sizes: string[], colors: string[], variants: object[] }} value
 * @param {(next: object) => void} onChange
 */
export default function ProductVariantsEditor({ value, onChange }) {
  const { t } = useTranslation();
  const sizes = value?.sizes || [];
  const colors = value?.colors || [];
  const variants = value?.variants || [];
  const [customColor, setCustomColor] = React.useState('');

  const hasStructuredVariants = variants.length > 0;
  const columns = useMemo(() => getVariantColumns(variants), [variants]);

  const update = (patch) => onChange({ sizes, colors, variants, ...patch });

  const toggleSize = (size) => {
    const next = sizes.includes(size) ? sizes.filter((s) => s !== size) : [...sizes, size];
    update({ sizes: next });
  };

  const toggleColor = (colorValue) => {
    const next = colors.includes(colorValue)
      ? colors.filter((c) => c !== colorValue)
      : [...colors, colorValue];
    update({ colors: next });
  };

  const updateVariantCell = (rowIndex, key, val) => {
    const next = variants.map((row, i) => (i === rowIndex ? { ...row, [key]: val } : row));
    update({ variants: next });
  };

  const addVariantRow = () => {
    update({ variants: [...variants, emptyVariantRow(variants[0])] });
  };

  const removeVariantRow = (index) => {
    update({ variants: variants.filter((_, i) => i !== index) });
  };

  const clearStructuredVariants = () => {
    update({ variants: [] });
  };

  const colLabel = (key) => VARIANT_LABELS[key] || (key === 'stock' ? 'Stok' : key === 'price' ? 'Fiyat' : key === 'barcode' ? 'Barkod' : key === 'sku' ? 'SKU' : key);

  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="product-variants-editor">
      <div>
        <Label className="text-base font-medium">
          {t('seller.productVariants', 'Ürün varyantları')}
        </Label>
        <p className="text-xs text-muted-foreground mt-1">
          {hasStructuredVariants
            ? t('seller.variantsTableHint', 'Her satır bir varyant kombinasyonudur (renk/beden/stok).')
            : t('seller.productVariantsDesc', 'Beden ve renk seçerek varyant oluşturun.')}
        </p>
      </div>

      {hasStructuredVariants ? (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm min-w-[480px]">
              <thead className="bg-muted/50">
                <tr>
                  {columns.map((col) => (
                    <th key={col} className="px-2 py-1.5 text-left text-xs font-medium">
                      {colLabel(col)}
                    </th>
                  ))}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {variants.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-t">
                    {columns.map((col) => (
                      <td key={col} className="px-1 py-1">
                        <Input
                          className="h-8 text-xs"
                          type={col === 'stock' || col === 'price' ? 'number' : 'text'}
                          step={col === 'price' ? '0.01' : undefined}
                          value={row[col] ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const val =
                              col === 'stock'
                                ? raw === '' ? '' : parseInt(raw, 10)
                                : col === 'price'
                                  ? raw === '' ? '' : parseFloat(raw)
                                  : raw;
                            updateVariantCell(rowIndex, col, val);
                          }}
                          data-testid={`variant-cell-${rowIndex}-${col}`}
                        />
                      </td>
                    ))}
                    <td className="px-1 py-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => removeVariantRow(rowIndex)}
                        aria-label="Satırı sil"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addVariantRow}>
              <Plus className="h-4 w-4 mr-1" />
              {t('seller.addVariantRow', 'Varyant satırı ekle')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={clearStructuredVariants}>
              {t('seller.switchToSimpleVariants', 'Basit beden/renk moduna geç')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Label>{t('seller.sizes', 'Bedenler')}</Label>
            <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg mt-1 max-h-32 overflow-y-auto">
              {AVAILABLE_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => toggleSize(size)}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sizes.includes(size)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-secondary'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>{t('seller.colors', 'Renkler')}</Label>
            <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg mt-1">
              {PREDEFINED_COLORS.map((color) => {
                const colorValue = `${color.name} (${color.value})`;
                const selected = colors.some((c) => c.startsWith(color.name));
                return (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => toggleColor(colorValue)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm border-2 ${
                      selected ? 'border-primary bg-primary/10' : 'border-transparent bg-background'
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: color.value }}
                    />
                    {color.name}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder={t('seller.customColorPlaceholder', 'Özel renk adı')}
                className="flex-1 h-9"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  if (customColor.trim()) {
                    update({ colors: [...colors, customColor.trim()] });
                    setCustomColor('');
                  }
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {colors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {colors.map((color, index) => (
                  <span
                    key={`${color}-${index}`}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-xs"
                  >
                    {color}
                    <button type="button" onClick={() => update({ colors: colors.filter((_, i) => i !== index) })}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => update({ variants: [emptyVariantRow()] })}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('seller.useDetailedVariants', 'Detaylı varyant tablosu kullan')}
          </Button>
        </>
      )}
    </div>
  );
}

export function buildVariantsPayload({ sizes = [], colors = [], variants = [] }) {
  const payload = {
    sizes: Array.isArray(sizes) ? sizes : [],
    colors: Array.isArray(colors) ? colors : [],
    variants: Array.isArray(variants) ? variants : [],
  };
  if (payload.variants.length > 0) {
    const total = payload.variants.reduce((sum, v) => sum + (parseInt(v?.stock, 10) || 0), 0);
    if (total > 0) payload._computedStock = total;
  }
  return payload;
}
